import { IClassificacao } from "app/types/IClassificacao"
import React, { memo, useCallback, useEffect, useMemo, useState } from "react"

const estiloAbaClassificacao = require("./abaClassificacao.module.scss").default

const useRenderizarLinhasAposEntrada = () => {
  const [renderizarLinhas, setRenderizarLinhas] = useState(false)

  useEffect(() => {
    if (typeof window !== "undefined" && window.requestAnimationFrame) {
      const frame = window.requestAnimationFrame(() => setRenderizarLinhas(true))
      return () => window.cancelAnimationFrame(frame)
    }

    const timeout = setTimeout(() => setRenderizarLinhas(true), 0)
    return () => clearTimeout(timeout)
  }, [])

  return renderizarLinhas
}

export type AbaClassificacaoProps = {
  somenteLeitura: boolean
  temPermissaoAlterar: boolean
  classificacoesDisponiveis: IClassificacao[]
  classificacoesSelecionadas: IClassificacao[]
  onRemoverClassificacoesSelecionadas: (selecionados: IClassificacao) => void
  onIncluirClassificacoesSelecionadas: (selecionados: IClassificacao) => void
}

export const AbaClassificacao = memo((props: AbaClassificacaoProps) => {
  const renderizarLinhas = useRenderizarLinhasAposEntrada()
  const podeAlterar = !props.somenteLeitura && props.temPermissaoAlterar

  const handleRemoverClassificacao = useCallback((classificacao: IClassificacao) => {
    props.onRemoverClassificacoesSelecionadas(classificacao)
  }, [props.onRemoverClassificacoesSelecionadas])

  const handleIncluirClassificacao = useCallback((classificacao: IClassificacao) => {
    props.onIncluirClassificacoesSelecionadas(classificacao)
  }, [props.onIncluirClassificacoesSelecionadas])

  const classificacoesDisponiveisOrdenadas = useMemo(() => {
    if (!renderizarLinhas) {
      return []
    }

    return [...(props.classificacoesDisponiveis ?? [])].sort((a, b) => a.Nome.localeCompare(b.Nome))
  }, [renderizarLinhas, props.classificacoesDisponiveis])

  const headerSelecionado = (
    <div className="_table-header-group">
      <div className="_table-row">
        {podeAlterar && (
          <div 
            className={`_table-cell _w-8 ${estiloAbaClassificacao.tableCellHeader}`}
            id="AbaClassificacao_HeaderSelecionadas_Checkbox"
          />
        )}
        <div 
          className={`_table-cell _font-bold ${estiloAbaClassificacao.tableCellHeader}`}
          id="AbaClassificacao_HeaderSelecionadas_Titulo"
        >
          Selecionadas
        </div>
      </div>
    </div>
  )

  const bodyTabelaSelecionados = (classificacao: IClassificacao) => (
    <div 
      key={`AbaClassificacao_Selecionada_${classificacao.Codigo}`}
      className={`_table-row ${estiloAbaClassificacao.tableBody}`}
      id={`AbaClassificacao_Selecionada_${classificacao.Codigo}`}
    >
      {podeAlterar && (
        <div className={`_table-cell _w-8 _text-center ${estiloAbaClassificacao.tableCellBody}`}>
          <input
            type="checkbox"
            id={`AbaClassificacao_CheckboxRemover_${classificacao.Codigo}`}
            checked
            onChange={() => handleRemoverClassificacao(classificacao)}
          />
        </div>
      )}
      <div className={`_table-cell ${estiloAbaClassificacao.tableCellBody}`}>
        {classificacao.Nome}
      </div>
    </div>
  )

  const headerDisponiveis = (
    <div className="_table-header-group">
      <div className="_table-row">
        {podeAlterar && (
          <div 
            className={`_table-cell _w-8 ${estiloAbaClassificacao.tableCellHeader}`}
            id="AbaClassificacao_HeaderDisponiveis_Checkbox"
          />
        )}
        <div 
          className={`_table-cell _font-bold ${estiloAbaClassificacao.tableCellHeader}`}
          id="AbaClassificacao_HeaderDisponiveis_Titulo"
        >
          Disponiveis
        </div>
      </div>
    </div>
  )

  const bodyTabelaDisponiveis = (classificacao: IClassificacao) => (
    <div 
      key={`AbaClassificacao_Disponivel_${classificacao.Codigo}`}
      className={`_table-row ${estiloAbaClassificacao.tableBody}`}
      id={`AbaClassificacao_Disponivel_${classificacao.Codigo}`}
    >
      {podeAlterar && (
        <div className={`_table-cell _text-center _w-8 ${estiloAbaClassificacao.tableCellBody}`}>
          <input
            type="checkbox"
            id={`AbaClassificacao_CheckboxIncluir_${classificacao.Codigo}`}
            onChange={() => handleIncluirClassificacao(classificacao)}
          />
        </div>
      )}
      <div className={`_table-cell ${estiloAbaClassificacao.tableCellBody}`}>
        {classificacao.Nome}
      </div>
    </div>
  )

  return (
    <React.Fragment>
      <div className="_table _w-full _border-collapse" id="AbaClassificacao_TabelaSelecionadas">
        {headerSelecionado}
        <div className="_table-row-group">
          {renderizarLinhas && props.classificacoesSelecionadas?.map((selecionados) => (
            bodyTabelaSelecionados(selecionados)
          ))}
        </div>
      </div>

      <div className="_table _w-full _mt-4 _border-collapse" id="AbaClassificacao_TabelaDisponiveis">
        {headerDisponiveis}
        <div className="_table-row-group">
          {renderizarLinhas && classificacoesDisponiveisOrdenadas.map((disponiveis) => (
            bodyTabelaDisponiveis(disponiveis)
          ))}
        </div>
      </div>
    </React.Fragment>
  )
})
